import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  ShieldAlert,
  ShieldCheck,
  Sparkles,
  Loader2,
  ArrowRight,
  FolderOpenDot,
  Scan,
  Wand2,
  Check,
  X,
  Info,
} from "lucide-react";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover";

export default function SecurityView() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [selected, setSelected] = useState(null);
  const [report, setReport] = useState(null);
  const [scanning, setScanning] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/projects");
        setProjects(data.projects || []);
      } catch {
        toast.error("No se pudieron cargar tus proyectos");
      } finally {
        setLoadingProjects(false);
      }
    })();
  }, []);

  const runScan = async (project) => {
    if (!project) return;
    setScanning(true);
    setReport(null);
    setSelected(project);
    try {
      const { data } = await api.post(`/projects/${project.project_id}/security/scan`, {});
      setReport(data);
    } catch (e) {
      const msg = e?.response?.data?.detail || "No se pudo analizar el texto";
      toast.error(msg);
      setReport(null);
    } finally {
      setScanning(false);
    }
  };

  const applyRewrite = async (paragraphIndex, newText) => {
    if (!selected || !report) return;
    const next = report.paragraphs.map((p, i) =>
      i === paragraphIndex ? { ...p, text: newText, risk: 0, reasons: [], flagged: false } : p
    );
    const newContent = next.map((p) => p.text).join("\n\n");
    try {
      await api.patch(`/projects/${selected.project_id}`, { content_text: newContent });
      // re-scan automatically so the overall score updates
      await runScan(selected);
      toast.success("Reescritura aplicada al editor");
    } catch (e) {
      toast.error("No se pudo aplicar la reescritura");
    }
  };

  /* ---------- empty / picker view ---------- */
  if (!selected || !report) {
    return (
      <div className="px-6 lg:px-12 py-8 lg:py-10 max-w-5xl mx-auto" data-testid="security-view">
        <Intro />

        <section className="mt-8">
          <p className="text-[10px] tracking-[0.22em] uppercase text-copper font-medium mb-3">
            Elige el proyecto a analizar
          </p>
          {loadingProjects ? (
            <p className="text-ink-soft text-[13px]">Cargando proyectos…</p>
          ) : projects.length === 0 ? (
            <div className="rounded-2xl border border-rule bg-white p-6 text-center">
              <p className="font-serif text-xl text-ink">Aún no tienes proyectos.</p>
              <button
                onClick={() => navigate("/dashboard")}
                className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-[13px] font-medium text-paper bg-forest hover:bg-forest-dark"
              >
                Crear mi primer proyecto
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="security-projects">
              {projects.map((p) => (
                <button
                  key={p.project_id}
                  data-testid={`security-project-${p.project_id}`}
                  onClick={() => runScan(p)}
                  disabled={scanning}
                  className="group text-left rounded-xl border border-rule bg-white/60 hover:bg-white p-4 transition-all disabled:opacity-60"
                >
                  <span className="grid place-items-center h-8 w-8 rounded-lg bg-forest/10 text-forest">
                    <FolderOpenDot size={14} />
                  </span>
                  <p className="mt-3 font-serif text-[15px] text-ink leading-tight line-clamp-2">{p.title}</p>
                  <p className="mt-1 text-[11.5px] text-ink-soft">{p.career}</p>
                  <span className="mt-3 inline-flex items-center gap-1.5 text-[12px] font-medium text-forest group-hover:translate-x-0.5 transition-transform">
                    <Scan size={12} />
                    Analizar texto
                    <ArrowRight size={12} />
                  </span>
                </button>
              ))}
            </div>
          )}
        </section>

        {scanning && (
          <div
            data-testid="security-scanning"
            className="mt-6 inline-flex items-center gap-2 rounded-full border border-rule bg-white px-4 py-2 text-[12.5px] text-ink-soft"
          >
            <Loader2 size={14} className="animate-spin text-forest" /> Escaneando vocabulario y sintaxis…
          </div>
        )}
      </div>
    );
  }

  /* ---------- report view ---------- */
  const score = report.overall_risk;
  const threshold = report.threshold;
  const tone =
    score >= 70 ? "danger" : score >= 40 ? "warn" : "good";

  return (
    <div className="px-6 lg:px-12 py-6 lg:py-8 max-w-5xl mx-auto" data-testid="security-report">
      <button
        onClick={() => {
          setSelected(null);
          setReport(null);
        }}
        data-testid="security-back-btn"
        className="inline-flex items-center gap-1.5 text-[12.5px] text-ink-soft hover:text-forest"
      >
        ← Volver a proyectos
      </button>

      <header className="mt-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
        <div>
          <p className="text-[10px] tracking-[0.22em] uppercase text-copper font-medium">
            Métrica de Seguridad
          </p>
          <h1
            data-testid="security-project-title"
            className="mt-2 font-serif text-2xl sm:text-3xl text-ink leading-tight text-balance"
          >
            {selected.title}
          </h1>
          <p className="mt-2 text-[13px] text-ink-soft max-w-xl">{report.summary}</p>
        </div>
        <ScoreRing score={score} tone={tone} />
      </header>

      {/* Legend */}
      <div className="mt-6 flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-paper-2 px-3 py-1 text-[11px] text-ink-soft">
          <Info size={11} /> {report.paragraphs.length} párrafos · {report.total_words} palabras
        </span>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-[#FFF6D5] border border-[#E9CB6B]/60 px-3 py-1 text-[11px] text-[#8A6A1E]">
          <ShieldAlert size={11} /> Resaltado: párrafos con riesgo ≥ {threshold}%
        </span>
        <button
          onClick={() => runScan(selected)}
          data-testid="security-rescan-btn"
          disabled={scanning}
          className="ml-auto inline-flex items-center gap-1.5 rounded-full border border-rule bg-white px-3 py-1.5 text-[12px] text-ink-soft hover:text-forest hover:border-forest disabled:opacity-60"
        >
          {scanning ? <Loader2 size={12} className="animate-spin" /> : <Scan size={12} />}
          Re-escanear
        </button>
      </div>

      {/* Paragraph list */}
      <section className="mt-6 flex flex-col gap-3" data-testid="security-paragraphs">
        {report.paragraphs.length === 0 && (
          <p className="text-[13px] text-ink-soft">Tu documento no tiene texto suficiente para evaluar.</p>
        )}
        {report.paragraphs.map((p) => (
          <ParagraphCard
            key={p.index}
            paragraph={p}
            projectId={selected.project_id}
            onApply={applyRewrite}
          />
        ))}
      </section>
    </div>
  );
}

/* ---------- intro ---------- */
function Intro() {
  return (
    <>
      <p className="text-[11px] tracking-[0.22em] uppercase text-copper font-medium">
        Métrica de Seguridad
      </p>
      <h1 className="mt-2 font-serif text-3xl sm:text-4xl text-ink leading-tight text-balance">
        ¿Qué tan robótico suena tu texto?
      </h1>
      <p className="mt-3 text-[14px] text-ink-soft max-w-2xl">
        Analizamos vocabulario, sintaxis, voz pasiva y conectores típicos de IA para estimar la
        probabilidad de que tu texto sea marcado por detectores como Turnitin. Los párrafos densos
        se resaltan en amarillo y puedes pedir una reescritura humana en voz activa.
      </p>
    </>
  );
}

/* ---------- score ring ---------- */
function ScoreRing({ score, tone }) {
  const color =
    tone === "danger" ? "#B6533F" : tone === "warn" ? "#C28A2E" : "#3E5A40";
  const ringTextClass =
    tone === "danger" ? "text-[#B6533F]" : tone === "warn" ? "text-copper" : "text-forest";
  return (
    <div
      data-testid="security-overall-ring"
      className="relative h-28 w-28 shrink-0 grid place-items-center"
    >
      <svg viewBox="0 0 36 36" className="h-full w-full -rotate-90">
        <circle cx="18" cy="18" r="15.9" fill="none" stroke="#E8E6E1" strokeWidth="3.2" />
        <circle
          cx="18"
          cy="18"
          r="15.9"
          fill="none"
          stroke={color}
          strokeWidth="3.2"
          strokeDasharray={`${score}, 100`}
          strokeLinecap="round"
        />
      </svg>
      <div className="absolute inset-0 grid place-items-center text-center leading-none">
        <div>
          <p data-testid="security-overall-score" className={`font-serif text-3xl ${ringTextClass}`}>
            {score}
            <span className="text-base align-top">%</span>
          </p>
          <p className="mt-1 text-[9.5px] tracking-[0.18em] uppercase text-ink-soft">Riesgo IA</p>
        </div>
      </div>
    </div>
  );
}

/* ---------- paragraph card with hover/click rewrite ---------- */
function ParagraphCard({ paragraph, projectId, onApply }) {
  const [rewriting, setRewriting] = useState(false);
  const [suggestion, setSuggestion] = useState(null);
  const [open, setOpen] = useState(false);

  const flagged = paragraph.flagged;
  const baseClass = flagged
    ? "border-[#E9CB6B]/70 bg-[#FFF6D5]/60 hover:bg-[#FFF6D5]"
    : "border-rule bg-white hover:bg-white/90";
  const riskTextClass =
    paragraph.risk >= 70
      ? "text-[#B6533F]"
      : paragraph.risk >= 40
      ? "text-copper"
      : "text-forest";

  const handleRewrite = async () => {
    if (rewriting) return;
    setRewriting(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/security/rewrite`, {
        text: paragraph.text,
        reasons: paragraph.reasons || [],
      });
      setSuggestion(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo generar la reescritura");
    } finally {
      setRewriting(false);
    }
  };

  return (
    <div
      data-testid={`security-paragraph-${paragraph.index}`}
      data-flagged={flagged ? "true" : "false"}
      className={`rounded-xl border ${baseClass} transition-colors`}
    >
      <div className="p-4">
        <div className="flex items-center justify-between gap-3">
          <span className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.22em] uppercase text-ink-soft/80 font-medium">
            Párrafo {paragraph.index + 1}
          </span>
          <span
            data-testid={`security-paragraph-risk-${paragraph.index}`}
            className={`text-[11.5px] font-medium ${riskTextClass}`}
          >
            {paragraph.risk}% riesgo
          </span>
        </div>
        <p className="mt-2 text-[13.5px] text-ink leading-relaxed whitespace-pre-wrap">
          {paragraph.text}
        </p>

        {flagged && (
          <>
            {paragraph.reasons?.length > 0 && (
              <ul className="mt-3 flex flex-col gap-1">
                {paragraph.reasons.map((r, i) => (
                  <li
                    key={i}
                    className="inline-flex items-start gap-1.5 text-[11.5px] text-[#8A6A1E]"
                  >
                    <ShieldAlert size={11} className="mt-0.5 shrink-0" />
                    <span>{r}</span>
                  </li>
                ))}
              </ul>
            )}

            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                  <button
                    onMouseEnter={() => {
                      if (!suggestion && !rewriting) {
                        // pre-fetch on hover for nicer UX
                        handleRewrite();
                      }
                    }}
                    onClick={() => {
                      setOpen(true);
                      if (!suggestion && !rewriting) handleRewrite();
                    }}
                    data-testid={`security-rewrite-btn-${paragraph.index}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-forest text-paper text-[12px] font-medium hover:bg-forest-dark"
                  >
                    {rewriting ? <Loader2 size={12} className="animate-spin" /> : <Wand2 size={12} />}
                    Sugerir reescritura
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  align="start"
                  sideOffset={8}
                  className="w-[min(420px,92vw)] p-0 border-rule"
                  data-testid={`security-rewrite-popover-${paragraph.index}`}
                >
                  <div className="p-4">
                    <div className="flex items-center gap-2">
                      <span className="grid place-items-center h-7 w-7 rounded-md bg-forest text-paper">
                        <Sparkles size={12} />
                      </span>
                      <p className="font-serif text-base text-ink">Versión humana sugerida</p>
                    </div>
                    {rewriting && !suggestion && (
                      <div className="mt-3 inline-flex items-center gap-2 text-[12.5px] text-ink-soft">
                        <Loader2 size={12} className="animate-spin" /> Reescribiendo en voz activa…
                      </div>
                    )}
                    {suggestion && (
                      <>
                        <p
                          data-testid={`security-rewrite-text-${paragraph.index}`}
                          className="mt-3 text-[13px] text-ink leading-relaxed whitespace-pre-wrap"
                        >
                          {suggestion.rewrite}
                        </p>
                        {suggestion.rationale && (
                          <p className="mt-2 text-[11.5px] text-ink-soft italic">
                            {suggestion.rationale}
                          </p>
                        )}
                        <div className="mt-4 flex items-center justify-end gap-2">
                          <button
                            onClick={() => {
                              setSuggestion(null);
                              setOpen(false);
                            }}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[12px] text-ink-soft hover:bg-paper-2"
                          >
                            <X size={11} /> Descartar
                          </button>
                          <button
                            data-testid={`security-apply-rewrite-${paragraph.index}`}
                            onClick={() => {
                              onApply(paragraph.index, suggestion.rewrite);
                              setOpen(false);
                            }}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-forest text-paper text-[12px] font-medium hover:bg-forest-dark"
                          >
                            <Check size={12} /> Aplicar al editor
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </>
        )}

        {!flagged && paragraph.risk < 30 && paragraph.text.length > 0 && (
          <p className="mt-2 inline-flex items-center gap-1.5 text-[11.5px] text-forest">
            <ShieldCheck size={11} /> Suena humano y académico.
          </p>
        )}
      </div>
    </div>
  );
}
